import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOutletContext } from 'react-router-dom';
import { getDocuments, uploadDocument } from '../utils/api';

function DocumentDrawer({ doc, onClose }) {
  if (!doc) return null;
  const ext = (doc.original_name || doc.filename || '').split('.').pop().toLowerCase();
  const fileTypeColors = {
    pdf:  { color: 'var(--color-error)',   bg: '#FEF2F2', label: 'PDF'  },
    xlsx: { color: 'var(--color-success)', bg: '#ECFDF5', label: 'XLSX' },
    xls:  { color: 'var(--color-success)', bg: '#ECFDF5', label: 'XLS'  },
    docx: { color: 'var(--color-info)',    bg: '#EFF6FF', label: 'DOCX' },
    doc:  { color: 'var(--color-info)',    bg: '#EFF6FF', label: 'DOC'  },
  };
  const ft = fileTypeColors[ext] || fileTypeColors.pdf;
  return (
    <>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 100 }} onClick={onClose} />
      <div style={{ position: 'fixed', top: 0, right: 0, width: 480, height: '100vh', background: 'var(--color-surface)', boxShadow: 'var(--shadow-modal)', zIndex: 101, overflowY: 'auto', animation: 'slideInRight 250ms ease-out' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>Document Details</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--color-text-light)' }}>✕</button>
        </div>
        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
            <div style={{ width: 48, height: 48, borderRadius: 'var(--radius-md)', background: ft.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
              {ext === 'pdf' ? '📄' : ext === 'xlsx' || ext === 'xls' ? '📊' : '📋'}
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{doc.original_name || doc.filename}</div>
              <span style={{ padding: '2px 8px', borderRadius: 'var(--radius-sm)', fontSize: 11, fontWeight: 600, color: ft.color, background: ft.bg }}>{ft.label}</span>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[
              ['File Size', doc.file_size ? (doc.file_size / 1024 / 1024).toFixed(2) + ' MB' : '—'],
              ['Status', doc.parse_status || doc.status || 'processed'],
              ['Document ID', `#${doc.id}`],
              ['Uploaded', new Date(doc.uploaded_at || doc.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })],
              ['Type', doc.document_type || 'BOQ'],
            ].map(([label, value]) => (
              <div key={label} style={{ padding: '12px 14px', background: 'var(--color-background)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }}>
                <div style={{ fontSize: 11, color: 'var(--color-text-light)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500 }}>{label}</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-dark)' }}>{value}</div>
              </div>
            ))}
          </div>
          <div style={{ padding: '14px 16px', background: '#ECFDF5', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-success)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span>✓</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-success)' }}>Extraction complete</div>
              <div style={{ fontSize: 12, color: '#166534', marginTop: 2 }}>Items have been extracted and are ready to review</div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default function Documents() {
  const { selectedProject } = useOutletContext();
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [message, setMessage] = useState(null);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [drawerDoc, setDrawerDoc] = useState(null);
  const [showNextStep, setShowNextStep] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (selectedProject?.id) loadDocuments(selectedProject.id);
  }, [selectedProject]);

  async function loadDocuments(projectId) {
    try {
      const data = await getDocuments(projectId);
      setDocuments(data || []);
    } catch (err) { console.error(err); }
  }

  async function handleUpload(file) {
    if (!selectedProject) { setMessage({ type: 'error', text: 'Please select a project first' }); return; }
    setUploading(true);
    setExtracting(true);
    setMessage({ type: 'info', text: `AI is reading ${file.name} and extracting items...` });
    const formData = new FormData();
    formData.append('file', file);
    formData.append('project_id', selectedProject.id);
    formData.append('document_type', 'BOQ');
    try {
      const data = await uploadDocument(selectedProject.id, formData);
      setExtracting(false);
      setMessage({ type: 'success', text: `✓ ${file.name} processed — ${data.items_extracted || 0} items extracted` });
      setShowNextStep(true);
      loadDocuments(selectedProject.id);
    } catch (err) {
      setExtracting(false);
      setMessage({ type: 'error', text: err.message || 'Upload failed' });
    }
    setUploading(false);
  }

  function handleDrop(e) { e.preventDefault(); setDragOver(false); const file = e.dataTransfer.files[0]; if (file) handleUpload(file); }
  function handleFileInput(e) { const file = e.target.files[0]; if (file) handleUpload(file); e.target.value = ''; }
  function toggleDoc(id) { setSelectedDocs(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]); }
  function toggleAll() { setSelectedDocs(selectedDocs.length === documents.length ? [] : documents.map(d => d.id)); }

  const fileTypeColors = {
    pdf:  { color: 'var(--color-error)',   bg: '#FEF2F2', label: 'PDF'  },
    xlsx: { color: 'var(--color-success)', bg: '#ECFDF5', label: 'XLSX' },
    xls:  { color: 'var(--color-success)', bg: '#ECFDF5', label: 'XLS'  },
    docx: { color: 'var(--color-info)',    bg: '#EFF6FF', label: 'DOCX' },
    doc:  { color: 'var(--color-info)',    bg: '#EFF6FF', label: 'DOC'  },
  };

  return (
    <div className="page">
      <DocumentDrawer doc={drawerDoc} onClose={() => setDrawerDoc(null)} />

      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Documents</div>
          <div className="page-subtitle">Upload and manage project documents</div>
        </div>
      </div>

      {showNextStep && (
        <div style={{ padding: '14px 18px', borderRadius: 'var(--radius-md)', background: 'var(--color-teal-light)', border: '1px solid var(--color-teal)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span>✦</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-teal)' }}>Ready to build packages — review your extracted items first</div>
              <div style={{ fontSize: 12, color: 'var(--color-text-mid)', marginTop: 2 }}>AI has extracted items from your document</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => navigate('/extract')} className="btn btn-primary btn-sm">Review Items →</button>
            <button onClick={() => setShowNextStep(false)} className="btn btn-ghost btn-sm">✕</button>
          </div>
        </div>
      )}

      {message && (
        <div style={{ padding: '12px 16px', borderRadius: 'var(--radius-md)', fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, background: message.type === 'success' ? '#ECFDF5' : message.type === 'error' ? '#FEF2F2' : '#EFF6FF', color: message.type === 'success' ? 'var(--color-success)' : message.type === 'error' ? 'var(--color-error)' : 'var(--color-info)', border: `1px solid ${message.type === 'success' ? 'var(--color-success)' : message.type === 'error' ? 'var(--color-error)' : 'var(--color-info)'}` }}>
          {extracting && <span style={{ animation: 'spin 0.8s linear infinite', display: 'inline-block' }}>⟳</span>}
          {message.text}
        </div>
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><span style={{ fontWeight: 600 }}>Upload Project Documents</span></div>
        <div className="card-body">
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => !uploading && document.getElementById('fileInput').click()}
            style={{ border: `2px dashed ${dragOver ? 'var(--color-teal)' : 'var(--color-border)'}`, borderRadius: 'var(--radius-lg)', padding: '48px 24px', textAlign: 'center', cursor: uploading ? 'default' : 'pointer', background: dragOver ? 'var(--color-teal-light)' : 'var(--color-background)', transition: 'all 150ms' }}
          >
            <div style={{ fontSize: 36, marginBottom: 12, color: dragOver ? 'var(--color-teal)' : 'var(--color-text-light)' }}>↑</div>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{uploading ? 'Processing...' : 'Drop documents here or click to browse'}</div>
            <div style={{ color: 'var(--color-text-mid)', fontSize: 13, marginBottom: 14 }}>Drawings · BOQs · Specifications · Tender documents</div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
              {['PDF', 'XLSX', 'DOCX'].map(f => <span key={f} className="badge badge-teal">{f}</span>)}
            </div>
            {uploading && (
              <div style={{ maxWidth: 320, margin: '16px auto 0' }}>
                <div className="confidence-bar"><div className="confidence-bar-fill" style={{ width: extracting ? '80%' : '25%', background: 'var(--color-teal)', transition: 'width 3s ease' }} /></div>
                <div style={{ fontSize: 12, color: 'var(--color-text-mid)', marginTop: 8 }}>{extracting ? '✦ AI is extracting items...' : 'Uploading...'}</div>
              </div>
            )}
          </div>
          <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--color-background)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: 'var(--color-text-mid)' }}>Forward emails to:</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-teal)', fontFamily: 'var(--font-mono)' }}>{selectedProject ? `project-${selectedProject.id}@procura.tools` : 'select a project first'}</span>
          </div>
          <input id="fileInput" type="file" accept=".pdf,.xlsx,.xls,.docx" style={{ display: 'none' }} onChange={handleFileInput} disabled={uploading} />
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontWeight: 600 }}>Documents <span style={{ color: 'var(--color-text-light)', fontWeight: 400 }}>({documents.length})</span></span>
            {selectedDocs.length > 0 && <span style={{ fontSize: 12, color: 'var(--color-text-mid)' }}>{selectedDocs.length} selected</span>}
          </div>
          {documents.length > 0 && <button onClick={() => loadDocuments(selectedProject?.id)} className="btn btn-ghost btn-sm">↻ Refresh</button>}
        </div>
        {documents.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📂</div>
            <h3>No documents yet</h3>
            <p>Drag files above or forward emails to your project address</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 40 }}><input type="checkbox" checked={selectedDocs.length === documents.length && documents.length > 0} onChange={toggleAll} style={{ cursor: 'pointer', accentColor: 'var(--color-teal)' }} /></th>
                  {['File', 'Type', 'Size', 'Status', 'Uploaded', ''].map(h => <th key={h}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {documents.map(doc => {
                  const ext = (doc.original_name || doc.filename || '').split('.').pop().toLowerCase();
                  const ft = fileTypeColors[ext] || fileTypeColors.pdf;
                  const isSelected = selectedDocs.includes(doc.id);
                  return (
                    <tr key={doc.id} style={{ background: isSelected ? 'var(--color-teal-light)' : 'transparent', cursor: 'pointer' }}>
                      <td onClick={e => e.stopPropagation()}><input type="checkbox" checked={isSelected} onChange={() => toggleDoc(doc.id)} style={{ cursor: 'pointer', accentColor: 'var(--color-teal)' }} /></td>
                      <td onClick={() => setDrawerDoc(doc)}>
                        <div style={{ fontWeight: 500 }}>{doc.original_name || doc.filename}</div>
                        <div style={{ fontSize: 11, color: 'var(--color-text-light)', fontFamily: 'var(--font-mono)' }}>ID: {doc.id}</div>
                      </td>
                      <td onClick={() => setDrawerDoc(doc)}><span className="badge" style={{ color: ft.color, background: ft.bg }}>{ft.label}</span></td>
                      <td onClick={() => setDrawerDoc(doc)} style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}>{doc.file_size ? (doc.file_size / 1024 / 1024).toFixed(1) + ' MB' : '—'}</td>
                      <td onClick={() => setDrawerDoc(doc)}><span className="badge badge-success">{doc.parse_status || 'processed'}</span></td>
                      <td onClick={() => setDrawerDoc(doc)} style={{ fontSize: 13, color: 'var(--color-text-mid)' }}>{new Date(doc.uploaded_at || doc.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                      <td onClick={() => setDrawerDoc(doc)}><span style={{ fontSize: 12, color: 'var(--color-teal)', fontWeight: 500 }}>View →</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}